import React, { useRef, useState, useEffect, useCallback } from 'react';
import SignaturePadLib from 'signature_pad';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { RotateCcw, Check, PenTool, Type } from 'lucide-react';

export default function SignaturePad({ onSave, disabled = false }) {
  const canvasRef = useRef(null);
  const sigPadRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [mode, setMode] = useState('draw');
  const [typedName, setTypedName] = useState('');

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sigPadRef.current) return;

    const container = canvas.parentElement;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = container.offsetWidth * ratio;
    canvas.height = 192 * ratio;
    canvas.style.width = container.offsetWidth + 'px';
    canvas.style.height = '192px';
    canvas.getContext('2d').scale(ratio, ratio);

    // Fill with white background
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    sigPadRef.current.clear();
    setIsEmpty(true);
  }, []);

  useEffect(() => {
    if (mode !== 'draw' || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const sigPad = new SignaturePadLib(canvas, {
      penColor: 'black',
      backgroundColor: 'rgb(255, 255, 255)',
    });

    sigPad.addEventListener('endStroke', () => {
      setIsEmpty(sigPad.isEmpty());
    });

    sigPadRef.current = sigPad;

    // Initial resize
    const container = canvas.parentElement;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = container.offsetWidth * ratio;
    canvas.height = 192 * ratio;
    canvas.style.width = container.offsetWidth + 'px';
    canvas.style.height = '192px';
    canvas.getContext('2d').scale(ratio, ratio);
    sigPad.clear();

    window.addEventListener('resize', resizeCanvas);

    return () => {
      sigPad.off();
      window.removeEventListener('resize', resizeCanvas);
      sigPadRef.current = null;
    };
  }, [mode, resizeCanvas]);

  const clear = () => {
    if (mode === 'draw') {
      sigPadRef.current?.clear();
      setIsEmpty(true);
    } else {
      setTypedName('');
    }
  };

  const save = () => {
    if (mode === 'draw') {
      if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
        const signatureData = sigPadRef.current.toDataURL('image/png');
        onSave(signatureData);
      }
    } else {
      if (typedName.trim()) {
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 100;
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.fillStyle = 'black';
        ctx.font = 'italic 36px "Brush Script MT", cursive, serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);

        const signatureData = canvas.toDataURL('image/png');
        onSave(signatureData);
      }
    }
  };

  const canSave = mode === 'draw' ? !isEmpty : typedName.trim().length > 0;

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex gap-2 mb-4">
        <Button
          type="button"
          variant={mode === 'draw' ? 'default' : 'outline'}
          onClick={() => setMode('draw')}
          className={`flex-1 ${mode === 'draw' ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-600 text-gray-300 hover:bg-gray-800'}`}
        >
          <PenTool className="w-4 h-4 mr-2" />
          Draw Signature
        </Button>
        <Button
          type="button"
          variant={mode === 'type' ? 'default' : 'outline'}
          onClick={() => setMode('type')}
          className={`flex-1 ${mode === 'type' ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-600 text-gray-300 hover:bg-gray-800'}`}
        >
          <Type className="w-4 h-4 mr-2" />
          Type Name
        </Button>
      </div>

      {mode === 'draw' ? (
        <>
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
            <PenTool className="w-4 h-4" />
            <span>Draw your signature below</span>
          </div>

          {/* Signature Canvas */}
          <div className="relative border-2 border-dashed border-gray-600 rounded-lg bg-white overflow-hidden">
            <canvas
              ref={canvasRef}
              style={{ width: '100%', height: '192px', touchAction: 'none' }}
            />

            {/* Signature line */}
            <div className="absolute bottom-8 left-8 right-8 border-b border-gray-300" />
            <div className="absolute bottom-2 left-8 text-xs text-gray-400">
              Sign above the line
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
            <Type className="w-4 h-4" />
            <span>Type your full legal name to sign</span>
          </div>

          {/* Typed Signature Input */}
          <div className="space-y-4">
            <Input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Enter your full legal name"
              className="text-lg py-6 bg-gray-800 border-gray-600 text-white"
              disabled={disabled}
            />

            {/* Preview of typed signature */}
            {typedName && (
              <div className="bg-white rounded-lg p-4 border-2 border-dashed border-gray-300">
                <p className="text-gray-400 text-xs mb-2">Signature Preview:</p>
                <p
                  className="text-3xl text-center text-black"
                  style={{ fontFamily: '"Brush Script MT", cursive, serif', fontStyle: 'italic' }}
                >
                  {typedName}
                </p>
                <div className="border-b border-gray-300 mt-4 mx-8" />
              </div>
            )}
          </div>
        </>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={clear}
          disabled={disabled}
          className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Clear
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={disabled || !canSave}
          className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 disabled:opacity-50"
        >
          <Check className="w-4 h-4 mr-2" />
          Confirm Signature
        </Button>
      </div>

      {!canSave && (
        <p className="text-xs text-yellow-500 text-center">
          {mode === 'draw'
            ? 'Please draw your signature above before confirming'
            : 'Please enter your full legal name to sign'}
        </p>
      )}

      <p className="text-xs text-gray-500 text-center mt-2">
        By signing, you agree that this electronic signature is legally binding and has the same effect as a handwritten signature.
      </p>
    </div>
  );
}
